// Custom hook for AppsFlyer Smart Script integration

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  loadAppsFlyerSmartScript,
  isSmartScriptReady,
  initAndGenerateOneLinkURL,
} from 'shared/components/AppMigration/helpers';

interface IUseAppsFlyerSmartScriptOptions {
  /**
   * Whether to auto-load the script on mount
   * @default true
   */
  autoLoad?: boolean;
  /**
   * Additional parameters to pass to the OneLink URL
   */
  additionalParams?: Record<string, string>;
}

interface IUseAppsFlyerSmartScriptReturn {
  /**
   * Whether the Smart Script is loaded and ready
   */
  isReady: boolean;
  /**
   * Whether the script is currently loading
   */
  isLoading: boolean;
  /**
   * Error if script loading failed
   */
  error: Error | null;
  /**
   * The generated OneLink URL (null until generated)
   */
  oneLinkURL: string | null;
  /**
   * Manually load the script
   */
  loadScript: () => Promise<void>;
  /**
   * Generate a new OneLink URL with optional additional params
   */
  generateURL: (params?: Record<string, string>) => Promise<string>;
}

/**
 * Custom hook for managing AppsFlyer Smart Script
 *
 * Features:
 * - Auto-loads the script on mount (configurable)
 * - Provides functions to generate OneLink URLs
 * - Falls back to manual URL generation if script fails
 *
 * @param options - Configuration options
 * @returns Script state and utility functions
 */
export const useAppsFlyerSmartScript = (
  options: IUseAppsFlyerSmartScriptOptions = {}
): IUseAppsFlyerSmartScriptReturn => {
  const { autoLoad = true, additionalParams } = options;

  const [isReady, setIsReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [oneLinkURL, setOneLinkURL] = useState<string | null>(null);

  // Track if component is mounted to prevent state updates after unmount
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Load script function
  const loadScript = useCallback(async () => {
    if (typeof window === 'undefined') return;

    // Check if already ready
    if (isSmartScriptReady()) {
      setIsReady(true);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await loadAppsFlyerSmartScript();

      if (isMounted.current) {
        const ready = isSmartScriptReady();
         setIsReady(ready);
        setIsLoading(false);
        // Optionally re-check readiness on the next tick in case initialization is deferred
         if (!ready) {
           setTimeout(() => {
             if (!isMounted.current) return;
             setIsReady(isSmartScriptReady());
           }, 0);
         }
      }
    } catch (err) {
      if (isMounted.current) {
        setError(
          err instanceof Error ? err : new Error('Failed to load Smart Script')
        );
        setIsLoading(false);
      }
    }
  }, []);

  // Generate OneLink URL function
  const generateURL = useCallback(
    async (params?: Record<string, string>): Promise<string> => {
      const mergedParams = { ...additionalParams, ...params };
      const url = await initAndGenerateOneLinkURL(mergedParams);

      if (isMounted.current) {
        setOneLinkURL(url);
      }

      return url;
    },
    [additionalParams]
  );

  // Auto-load on mount if enabled
  useEffect(() => {
    if (autoLoad && typeof window !== 'undefined') {
      loadScript();
    }
  }, [autoLoad, loadScript]);

  return {
    isReady,
    isLoading,
    error,
    oneLinkURL,
    loadScript,
    generateURL,
  };
};

export default useAppsFlyerSmartScript;
